import { useCallback, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { notificationsService } from "../services/notifications.service";
import type { AssignmentNotice } from "../types/casePathologistAssignment";
import {
  assignmentNoticeInvalidationTargets,
  casePathologistAssignmentQueryKeys,
} from "../queryKeys/casePathologistAssignmentQueryKeys";
import {
  loadAssignmentNoticePoll,
  selectUnseenNotices,
} from "./pathologistAssignmentNoticePolling";

const NOTICE_POLL_INTERVAL_MS = 5000;

interface AssignmentNoticeOptions {
  enabled: boolean;
}

export function usePathologistAssignmentNotices({
  enabled,
}: AssignmentNoticeOptions) {
  const queryClient = useQueryClient();
  const cursorRef = useRef<string>();
  const seenNoticeIdsRef = useRef(new Set<string>());
  const seenNoticeOrderRef = useRef<string[]>([]);
  const [notices, setNotices] = useState<AssignmentNotice[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);

  const invalidateNoticeTargets = useCallback(
    async (noticesToInvalidate: AssignmentNotice[]) => {
      const targets = assignmentNoticeInvalidationTargets(
        noticesToInvalidate.map(({ caseId }) => caseId)
      );
      await Promise.all(
        targets.map((target) => queryClient.invalidateQueries(target))
      );
    },
    [queryClient]
  );

  const noticesQuery = useQuery({
    queryKey: casePathologistAssignmentQueryKeys.notices,
    queryFn: async () => {
      const page = await loadAssignmentNoticePoll(cursorRef.current);
      const newNotices = selectUnseenNotices(
        page.data,
        seenNoticeIdsRef.current,
        seenNoticeOrderRef.current
      );
      if (newNotices.length > 0) {
        setNotices((current) =>
          [...newNotices.slice().reverse(), ...current].slice(0, 50)
        );
        await invalidateNoticeTargets(newNotices);
      }
      setUnreadCount(page.unreadCount);
      cursorRef.current = page.nextCursor;
      return page;
    },
    enabled,
    refetchInterval: NOTICE_POLL_INTERVAL_MS,
    refetchIntervalInBackground: false,
  });

  const markRead = useCallback(async (notificationId: string) => {
    const updatedNotice =
      await notificationsService.markPathologistAssignmentNoticeRead(
        notificationId
      );
    setNotices((current) =>
      current.map((notice) =>
        notice.id === updatedNotice.id ? updatedNotice : notice
      )
    );
    setUnreadCount((current) => Math.max(0, current - 1));
  }, []);

  return {
    notices,
    unreadCount,
    isLoading: noticesQuery.isLoading,
    markRead,
  };
}
